import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { formatCurrency, formatDateTime } from "../lib/utils";
import USDHint from "../components/USDHint";
import PriceInput from "../components/PriceInput";
import toast from "react-hot-toast";
import axios from "axios";
import { Plus, Users, Phone, MapPin, CreditCard, DollarSign, Trash2, Search, Crown, Star, Gift, Award, Coins, FileSpreadsheet, MessageCircle, Bell, Send } from "lucide-react";
import { exportCustomersToExcel } from "../utils/excelExport";

const API = `${window.location.origin}/api`;

export default function CustomersPage() {
  const { } = useAuth();
  const language = "ar";
  const { t } = useTranslation(language);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isPointsDialogOpen, setIsPointsDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    email: "",
  });
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [pointsAmount, setPointsAmount] = useState("");
  const [pointsAction, setPointsAction] = useState("add"); // add or redeem
  const [customerStats, setCustomerStats] = useState(null);
  const [shopInfo, setShopInfo] = useState(null);

  const isArabic = language === "ar";
  
  // Calculate VIP threshold (customers with total purchases > 500,000 د.ع)
  const VIP_THRESHOLD = 500000;

  useEffect(() => {
    fetchCustomers();
    fetchCustomerStats();
    fetchShopInfo();
  }, []);

  const fetchShopInfo = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setShopInfo(response.data);
    } catch (error) {
      console.error("Error fetching shop info:", error);
    }
  };

  // 📱 إرسال تذكير الدين عبر WhatsApp
  const sendDebtReminder = (customer) => {
    if (!customer.phone) {
      toast.error(isArabic ? "العميل ليس لديه رقم هاتف" : "Customer has no phone number");
      return;
    }

    // تنظيف رقم الهاتف
    let phone = customer.phone.replace(/\D/g, '');
    if (phone.startsWith('07')) {
      phone = '964' + phone.substring(1);
    } else if (!phone.startsWith('964')) {
      phone = '964' + phone;
    }

    const formatNum = (n) => new Intl.NumberFormat('en-US').format(n || 0);
    
    let message = isArabic 
      ? `🔔 *تذكير ودي بالدين*\n`
      : `🔔 *Friendly Debt Reminder*\n`;
    
    message += `━━━━━━━━━━━━━━━\n`;
    message += isArabic ? `مرحباً ${customer.name} 👋\n\n` : `Hello ${customer.name} 👋\n\n`;
    
    message += isArabic
      ? `نود تذكيرك بأن لديك مبلغ مستحق:\n`
      : `We would like to remind you of your outstanding balance:\n`;
    
    message += `💰 *${formatNum(customer.debt)} د.ع*\n\n`;
    
    message += isArabic
      ? `نرجو تسديد المبلغ في أقرب وقت ممكن.\n`
      : `Please settle the amount at your earliest convenience.\n`;
    
    message += `━━━━━━━━━━━━━━━\n`;
    message += `🏪 ${shopInfo?.shop_name || 'المتجر'}\n`;
    message += `📞 ${shopInfo?.phone || ''}\n`;
    message += isArabic ? `\nشكراً لتعاملكم معنا! 🙏` : `\nThank you for your business! 🙏`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    
    toast.success(isArabic ? "جاري فتح WhatsApp..." : "Opening WhatsApp...");
  };

  // 📱 إرسال تذكيرات جماعية لجميع المدينين
  const sendBulkDebtReminders = () => {
    const debtors = customers.filter(c => c.debt > 0 && c.phone);
    
    if (debtors.length === 0) {
      toast.error(isArabic ? "لا يوجد عملاء مدينين لديهم أرقام هواتف" : "No debtors with phone numbers");
      return;
    }

    const formatNum = (n) => new Intl.NumberFormat('en-US').format(n || 0);
    const totalDebt = debtors.reduce((sum, c) => sum + (c.debt || 0), 0);
    
    // إنشاء رسالة ملخص للمدير
    let summaryMessage = isArabic 
      ? `📊 *ملخص الديون المستحقة*\n`
      : `📊 *Outstanding Debts Summary*\n`;
    
    summaryMessage += `━━━━━━━━━━━━━━━\n`;
    summaryMessage += isArabic 
      ? `📌 عدد المدينين: ${debtors.length}\n`
      : `📌 Debtors: ${debtors.length}\n`;
    summaryMessage += isArabic
      ? `💰 إجمالي الديون: ${formatNum(totalDebt)} د.ع\n`
      : `💰 Total Debt: ${formatNum(totalDebt)} د.ع\n`;
    summaryMessage += `━━━━━━━━━━━━━━━\n\n`;
    
    debtors.slice(0, 10).forEach((c, i) => {
      summaryMessage += `${i + 1}. ${c.name}: ${formatNum(c.debt)} د.ع\n`;
    });
    
    if (debtors.length > 10) {
      summaryMessage += isArabic 
        ? `\n... و ${debtors.length - 10} آخرين`
        : `\n... and ${debtors.length - 10} more`;
    }

    const encodedMessage = encodeURIComponent(summaryMessage);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    
    toast.success(isArabic ? "جاري فتح WhatsApp مع ملخص الديون..." : "Opening WhatsApp with debt summary...");
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error(t("error"));
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerStats = async () => {
    try {
      const response = await axios.get(`${API}/analytics/customer-insights`);
      setCustomerStats(response.data);
    } catch (error) {
      console.error("Error fetching customer stats:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/customers`, formData);
      const isOffline = res.data?._offline_pending === true;
      toast.success(
        isOffline
          ? (language === "ar" ? "💾 تم الحفظ محلياً — سيُرسل عند رجوع الإنترنت" : "💾 Saved locally")
          : (language === "ar" ? "تم الإضافة بنجاح" : "Added successfully")
      );
      fetchCustomers();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) return;
    try {
      await axios.post(`${API}/customers/${selectedCustomer.id}/payment`, {
        customer_id: selectedCustomer.id,
        amount: parseFloat(paymentAmount),
        notes: paymentNotes,
      });
      toast.success(language === "ar" ? "تم تسجيل الدفع" : "Payment recorded");
      fetchCustomers();
      setIsPaymentDialogOpen(false);
      setPaymentAmount("");
      setPaymentNotes("");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === "ar" ? "هل أنت متأكد؟" : "Are you sure?")) return;
    try {
      await axios.delete(`${API}/customers/${id}`);
      toast.success(language === "ar" ? "تم الحذف" : "Deleted");
      fetchCustomers();
    } catch (error) {
      toast.error(t("error"));
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setFormData({ name: "", phone: "", address: "", email: "" });
  };

  const openPaymentDialog = (customer) => {
    setSelectedCustomer(customer);
    setIsPaymentDialogOpen(true);
  };

  const openPointsDialog = (customer, action = "add") => {
    setSelectedCustomer(customer);
    setPointsAction(action);
    setPointsAmount("");
    setIsPointsDialogOpen(true);
  };

  const handlePointsAction = async (e) => {
    e.preventDefault();
    if (!selectedCustomer || !pointsAmount) return;
    
    const endpoint = pointsAction === "add" 
      ? `${API}/customers/${selectedCustomer.id}/add-points`
      : `${API}/customers/${selectedCustomer.id}/redeem-points`;
    
    try {
      const response = await axios.post(`${endpoint}?points=${parseInt(pointsAmount)}`);
      toast.success(response.data.message || (isArabic ? "تمت العملية بنجاح" : "Operation successful"));
      fetchCustomers();
      fetchCustomerStats();
      setIsPointsDialogOpen(false);
      setPointsAmount("");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    }
  };

  // Check if customer is VIP based on total purchases
  const isVIPCustomer = (customer) => {
    const customerData = customerStats?.top_customers?.find(c => c.id === customer.id);
    return customerData?.tier === "vip";
  };

  const getCustomerPoints = (customer) => {
    return customer.loyalty_points || 0;
  };

  const totalDebt = customers.reduce((sum, c) => sum + (c.debt || 0), 0);
  const totalPoints = customers.reduce((sum, c) => sum + (c.loyalty_points || 0), 0);
  const vipCount = customerStats?.summary?.vip_count || 0;

  // تصفية الزبائن بناءً على البحث
  const filteredCustomers = customers.filter(
    (c) =>
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phone?.includes(searchTerm) ||
      c.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">{t("customers")}</h1>
          <div className="flex flex-wrap gap-4 mt-2 text-sm">
            <span className="text-slate-400">
              {language === "ar" ? "إجمالي الديون:" : "Total Debt:"}{" "}
              <span className="text-red-400 font-semibold">{formatCurrency(totalDebt)}</span>
            </span>
            <span className="text-slate-400">
              {language === "ar" ? "العملاء VIP:" : "VIP Customers:"}{" "}
              <span className="text-amber-400 font-semibold">{vipCount}</span>
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          {/* زر إرسال تذكيرات الديون الجماعية */}
          {customers.some(c => c.debt > 0) && (
            <Button
              onClick={sendBulkDebtReminders}
              variant="outline"
              className="border-green-600 text-green-500 hover:bg-green-600 hover:text-white"
              data-testid="bulk-debt-reminder-btn"
            >
              <Bell className="w-4 h-4 me-2" />
              {language === "ar" ? "تذكير المدينين" : "Remind Debtors"}
            </Button>
          )}
          <Button
            onClick={() => {
              try {
                exportCustomersToExcel(customers, language);
                toast.success(language === "ar" ? "تم تصدير العملاء" : "Customers exported");
              } catch (e) {
                toast.error(e.message);
              }
            }}
            variant="outline"
            className="border-emerald-600 text-emerald-500 hover:bg-emerald-600 hover:text-white"
            data-testid="export-customers-btn"
          >
            <FileSpreadsheet className="w-4 h-4 me-2" />
            Excel
          </Button>
          <Button
            onClick={() => setIsDialogOpen(true)}
            className="bg-red-600 hover:bg-red-700 btn-lift"
            data-testid="add-customer-btn"
          >
            <Plus className="w-4 h-4 me-2" />
            {t("addCustomer")}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">{isArabic ? "إجمالي العملاء" : "Total Customers"}</p>
                <p className="text-xl font-bold text-white">{customers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Crown className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">{isArabic ? "عملاء VIP" : "VIP Customers"}</p>
                <p className="text-xl font-bold text-amber-400">{vipCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <Coins className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">{isArabic ? "إجمالي النقاط" : "Total Points"}</p>
                <p className="text-xl font-bold text-emerald-400">{totalPoints.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-500/10 to-pink-500/10 border-red-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <DollarSign className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">{isArabic ? "إجمالي الديون" : "Total Debts"}</p>
                <p className="text-xl font-bold text-red-400">{formatCurrency(totalDebt)}</p>
                <USDHint amount={totalDebt} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search Box */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="relative">
            <Search className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400`} />
            <Input
              placeholder={language === "ar" ? "بحث بالاسم، الهاتف، أو العنوان..." : "Search by name, phone, or address..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${language === "ar" ? "pr-10" : "pl-10"} bg-slate-900 border-slate-700 text-white`}
              data-testid="customer-search-input"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-red-600/30 border-t-red-600 rounded-full animate-spin" />
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">{searchTerm ? (language === "ar" ? "لا توجد نتائج" : "No results") : t("noData")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header border-slate-700">
                    <TableHead className="text-amber-500">{t("customerName")}</TableHead>
                    <TableHead className="text-amber-500">{t("phone")}</TableHead>
                    <TableHead className="text-amber-500">{isArabic ? "النقاط" : "Points"}</TableHead>
                    <TableHead className="text-amber-500">{t("debt")}</TableHead>
                    <TableHead className="text-amber-500">{t("actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.map((customer) => (
                    <TableRow key={customer.id} className="border-slate-700 hover:bg-slate-800/50">
                      <TableCell className="text-white font-medium">
                        <div className="flex items-center gap-2">
                          {customer.name}
                          {isVIPCustomer(customer) && (
                            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                              <Crown className="w-3 h-3 me-1" />
                              VIP
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300 font-mono">{customer.phone}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-400 font-semibold">
                            {getCustomerPoints(customer).toLocaleString()}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => openPointsDialog(customer, "add")}
                              className="w-6 h-6 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20"
                              title={isArabic ? "إضافة نقاط" : "Add Points"}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                            {getCustomerPoints(customer) > 0 && (
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => openPointsDialog(customer, "redeem")}
                                className="w-6 h-6 text-amber-400 hover:text-amber-300 hover:bg-amber-500/20"
                                title={isArabic ? "استبدال نقاط" : "Redeem Points"}
                              >
                                <Gift className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={customer.debt > 0 ? "text-red-500 font-bold" : "text-green-500"}>
                          {formatCurrency(customer.debt || 0)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {customer.debt > 0 && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openPaymentDialog(customer)}
                                className="border-green-600 text-green-500 hover:bg-green-600 hover:text-white"
                                data-testid={`pay-debt-${customer.id}`}
                              >
                                <DollarSign className="w-4 h-4 me-1" />
                                {t("payDebt")}
                              </Button>
                              {/* زر إرسال تذكير الدين */}
                              {customer.phone && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => sendDebtReminder(customer)}
                                  className="text-green-400 hover:text-green-300 hover:bg-green-500/20"
                                  title={isArabic ? "إرسال تذكير WhatsApp" : "Send WhatsApp reminder"}
                                  data-testid={`remind-debt-${customer.id}`}
                                >
                                  <MessageCircle className="w-4 h-4" />
                                </Button>
                              )}
                            </>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDelete(customer.id)}
                            className="text-slate-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Customer Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">{t("addCustomer")}</DialogTitle>
            <DialogDescription className="sr-only">
              {isArabic ? "أدخل معلومات العميل الجديد" : "Enter new customer information"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-200">{t("customerName")}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
                required
                data-testid="customer-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">{t("phone")}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
                required
                data-testid="customer-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">{t("address")}</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="customer-address-input"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="border-slate-600 text-slate-300">
                {t("cancel")}
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="save-customer-btn">
                {t("save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">
              {t("payDebt")} - {selectedCustomer?.name}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {isArabic ? "سجل دفعة لتقليل الدين" : "Record a payment to reduce debt"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePayment} className="space-y-4">
            <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700">
              <p className="text-slate-400 text-sm">{language === "ar" ? "الدين الحالي" : "Current Debt"}</p>
              <p className="text-2xl font-bold text-red-500">{formatCurrency(selectedCustomer?.debt || 0)}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">{t("amount")}</Label>
              <PriceInput
                value={parseFloat(paymentAmount) || 0}
                onChange={(v) => setPaymentAmount(String(v))}
                placeholder="0"
                testId="payment-amount-input"
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">{t("notes")}</Label>
              <Input
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="payment-notes-input"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsPaymentDialogOpen(false)} className="border-slate-600 text-slate-300">
                {t("cancel")}
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="confirm-payment-btn">
                {t("confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Loyalty Points Dialog */}
      <Dialog open={isPointsDialogOpen} onOpenChange={setIsPointsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {pointsAction === "add" ? (
                <><Coins className="w-5 h-5 text-emerald-500" /> {isArabic ? "إضافة نقاط" : "Add Points"}</>
              ) : (
                <><Gift className="w-5 h-5 text-amber-500" /> {isArabic ? "استبدال نقاط" : "Redeem Points"}</>
              )}
              {" - "}{selectedCustomer?.name}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {pointsAction === "add" 
                ? (isArabic ? "أضف نقاط ولاء للعميل" : "Add loyalty points to customer")
                : (isArabic ? "استبدل نقاط الولاء بخصم" : "Redeem loyalty points for discount")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePointsAction} className="space-y-4">
            <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-slate-400 text-sm">{isArabic ? "النقاط الحالية" : "Current Points"}</p>
                  <p className="text-2xl font-bold text-emerald-400">
                    {getCustomerPoints(selectedCustomer || {}).toLocaleString()}
                  </p>
                </div>
                {isVIPCustomer(selectedCustomer || {}) && (
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                    <Crown className="w-4 h-4 me-1" />
                    VIP
                  </Badge>
                )}
              </div>
            </div>
            
            {pointsAction === "redeem" && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <p className="text-amber-400 text-sm flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  {isArabic 
                    ? "كل 1 نقطة = 100 دينار خصم" 
                    : "Each 1 point = 100 د.ع discount"}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-slate-200">
                {pointsAction === "add" 
                  ? (isArabic ? "عدد النقاط للإضافة" : "Points to Add")
                  : (isArabic ? "عدد النقاط للاستبدال" : "Points to Redeem")}
              </Label>
              <Input
                type="number"
                min="1"
                max={pointsAction === "redeem" ? getCustomerPoints(selectedCustomer || {}) : undefined}
                value={pointsAmount}
                onChange={(e) => setPointsAmount(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                required
                data-testid="points-amount-input"
              />
            </div>
            
            {pointsAction === "redeem" && pointsAmount && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-emerald-400 text-sm">
                  {isArabic ? "قيمة الخصم:" : "Discount Value:"}{" "}
                  <span className="font-bold">{formatCurrency(parseInt(pointsAmount || 0) * 100)}</span>
                </p>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsPointsDialogOpen(false)} className="border-slate-600 text-slate-300">
                {t("cancel")}
              </Button>
              <Button 
                type="submit" 
                className={pointsAction === "add" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"}
                data-testid="confirm-points-btn"
              >
                {pointsAction === "add" 
                  ? (isArabic ? "إضافة" : "Add")
                  : (isArabic ? "استبدال" : "Redeem")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
